import {
  Menu,
  ChevronDown,
  LayoutDashboard,
  UserIcon,
  Settings,
  LogOut,
  Home,
  Info,
  HelpCircle,
  FileText,
  Shield,
  BookOpen,
  Users,
  Heart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Link, router, usePage } from '@inertiajs/react';
import logoImage from '@/assets/logo.png';
import { UserProps } from '@/types/questionnaire';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

type MenuLink = {
  label: string;
  href: string;
  alias?: string;
  external?: boolean;
  dropdown?: MenuLink[];
};

const menuLinks: MenuLink[] = [
  { label: 'Home', href: '/home' },
  {
    label: 'About',
    href: '/about',
    dropdown: [
      { label: 'About Us', href: '/about' },
      { label: 'Services', href: '/about/services' },
      { label: 'Executive Board', href: '/about/executive-board' },
      { label: 'Board of Directors', href: '/about/board-of-directors' },
    ],
  },
  { label: 'FAQs', href: '/faqs' },
  { label: 'Resources', href: '/resources' },
  { label: 'Application', href: '/questionnaire' },
  { label: 'Community', href: '/community' },
  { label: 'Donate', href: 'https://www.zeffy.com/en-US/donation-form/support-project-short', external: true },
  // { label: 'Browse Mentees', href: '/matches' },
  { label: 'Admin', href: '/admin' },
];
const SignInButton = () => (
  <Button
    variant="outline"
    onClick={() => router.visit('/session/new', { replace: true })}
  >
    Sign in
  </Button>
);

const SignupButton = () => (
  <Button onClick={() => router.visit('/registration/new', { replace: true })}>
    Sign up
  </Button>
);

const SignoutButton = () => (
  <Button variant={'destructive'} onClick={() => router.delete('/session')}>
    Logout
  </Button>
);

const UserMenu = ({ user, isDashboardActive }: { user: UserProps; isDashboardActive: boolean }) => {
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''
    }`.toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`relative h-9 w-9 rounded-full ${isDashboardActive ? 'ring-2 ring-primary ring-offset-2' : ''
            }`}
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs bg-primary/10">
              {initials || 'U'}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs leading-none text-muted-foreground truncate">
              {user.email_address}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {(user.role === 'mentor' || user.role === 'mentee') && user.admin_verified && (
          <>
            <DropdownMenuItem asChild>
              <Link href="/matches/dashboard" className="cursor-pointer">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Dashboard
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem disabled>
          <UserIcon className="mr-2 h-4 w-4" />
          Profile
          <span className="ml-auto text-xs text-muted-foreground">
            Coming soon
          </span>
        </DropdownMenuItem>

        <DropdownMenuItem disabled>
          <Settings className="mr-2 h-4 w-4" />
          Settings
          <span className="ml-auto text-xs text-muted-foreground">
            Coming soon
          </span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => router.delete('/session')}
          className="cursor-pointer text-red-600 focus:text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function Navbar() {
  const { current_user } = usePage<{ current_user: UserProps }>().props;
  const { url } = usePage();

  const isDashboardActive = url.startsWith('/matches/dashboard') || url.startsWith('/admin');

  const isActive = (href: string) => {
    // Exact match
    if (url === href) return true;

    // Special case: root path (/) should highlight Application (/questionnaire)
    if (url === '/' && href === '/questionnaire') return true;

    // For nested routes (but not home)
    // This handles all sub-routes like /questionnaire/form, /questionnaire/complete
    // /about/services, etc.
    if (href !== '/' && url.startsWith(href)) return true;

    return false;
  };

  return (
    <header className="w-full px-5 md:px-10">
      <div className="container flex items-center h-16 mx-auto">
        <div className="flex-1 md:flex-initial">
          <Link href="/questionnaire" className="flex items-center space-x-2">
            <img
              src={logoImage}
              alt="Project Short"
              className="object-contain w-auto h-12"
            />
          </Link>
        </div>

        <nav className="items-center justify-center hidden gap-6 md:flex flex-1 mx-8">
          {menuLinks
            .filter((menuLink) => {
              if (
                menuLink.label === 'Admin' &&
                current_user?.role !== 'admin'
              ) {
                return false;
              }
              if (
                menuLink.label === 'Browse Mentees' &&
                current_user?.role !== 'mentor'
              ) {
                return false;
              }
              return true;
            })
            .map((menuLink) => {
              const linkClasses = `text-sm font-medium transition-colors ${isActive(menuLink.href)
                ? 'text-primary underline underline-offset-4 decoration-2'
                : 'hover:text-primary'
                }`;

              if (menuLink.dropdown) {
                const isDropdownActive = menuLink.dropdown.some((item) =>
                  isActive(item.href)
                );

                return (
                  <DropdownMenu key={menuLink.href}>
                    <DropdownMenuTrigger
                      className={`flex items-center gap-1 text-sm font-medium transition-colors ${isDropdownActive
                        ? 'text-primary underline underline-offset-4 decoration-2'
                        : 'hover:text-primary'
                        }`}
                    >
                      {menuLink.label}
                      <ChevronDown className="h-3 w-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {menuLink.dropdown.map((item) => (
                        <DropdownMenuItem key={item.href} asChild>
                          <Link href={item.href} className="cursor-pointer">
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return menuLink.external ? (
                <a
                  key={menuLink.href}
                  href={menuLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClasses}
                >
                  {menuLink.label}
                </a>
              ) : (
                <Link
                  key={menuLink.href}
                  href={menuLink.href}
                  className={linkClasses}
                >
                  {menuLink.label}
                </Link>
              );
            })}
        </nav>

        <div className="items-center hidden gap-2 md:flex">
          {!current_user ? (
            <>
              <SignInButton />
              <SignupButton />
            </>
          ) : (
            <UserMenu user={current_user} isDashboardActive={isDashboardActive} />
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[320px] sm:w-[380px] p-0 flex flex-col"
          >
            <div className={`border-b p-6 ${isDashboardActive ? 'bg-accent' : ''
              }`}>
              {current_user ? (
                <div className="flex items-center gap-3">
                  <Avatar className={`h-10 w-10 ${isDashboardActive ? 'ring-2 ring-primary ring-offset-2' : ''
                    }`}>
                    <AvatarFallback className="bg-muted">
                      {`${current_user.first_name?.[0] || ''}${current_user.last_name?.[0] || ''}`.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">
                      {current_user.first_name} {current_user.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {current_user.email_address}
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-lg font-semibold">Project Short</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sign in to access your dashboard
                  </p>
                </div>
              )}
            </div>

            <nav className="flex-1 overflow-y-auto">
              {current_user &&
                (current_user.role === 'mentor' ||
                  current_user.role === 'mentee') &&
                current_user.admin_verified && (
                  <div className="border-b px-2 py-2">
                    <Link
                      href="/matches/dashboard"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive('/matches/dashboard')
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span className="text-sm font-medium">Dashboard</span>
                    </Link>
                  </div>
                )}

              <div className="px-2 py-4 space-y-1">
                {menuLinks
                  .filter((menuLink) => {
                    if (
                      menuLink.label === 'Admin' &&
                      current_user?.role !== 'admin'
                    ) {
                      return false;
                    }
                    if (
                      menuLink.label === 'Browse Mentees' &&
                      current_user?.role !== 'mentor'
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((menuLink) => {
                    const getIcon = (label: string) => {
                      switch (label) {
                        case 'Home':
                          return <Home className="h-4 w-4" />;
                        case 'About':
                          return <Info className="h-4 w-4" />;
                        case 'FAQs':
                          return <HelpCircle className="h-4 w-4" />;
                        case 'Application':
                          return <FileText className="h-4 w-4" />;
                        case 'Admin':
                          return <Shield className="h-4 w-4" />;
                        case 'Community':
                          return <Users className='h-4 w-4' />
                        case 'Resources':
                          return <BookOpen className='h-4 w-4' />
                        case 'Donate':
                          return <Heart className='h-4 w-4' />
                        default:
                          return null;
                      }
                    };

                    if (menuLink.dropdown) {
                      return (
                        <div key={menuLink.href} className="space-y-1">
                          <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground">
                            {getIcon(menuLink.label)}
                            <span>{menuLink.label}</span>
                          </div>
                          <div className="ml-6 space-y-1">
                            {menuLink.dropdown.map((item) => (
                              <Link
                                key={item.href}
                                href={item.href}
                                className={`block px-3 py-1.5 text-sm rounded-md transition-colors ${isActive(item.href)
                                  ? 'bg-accent text-accent-foreground font-medium'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                  }`}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return menuLink.external ? (
                      <a
                        key={menuLink.href}
                        href={menuLink.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${isActive(menuLink.href)
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                      >
                        {getIcon(menuLink.label)}
                        <span>{menuLink.label}</span>
                      </a>
                    ) : (
                      <Link
                        key={menuLink.href}
                        href={menuLink.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${isActive(menuLink.href)
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                      >
                        {getIcon(menuLink.label)}
                        <span>{menuLink.label}</span>
                      </Link>
                    );
                  })}
              </div>
            </nav>

            {/* Footer Actions */}
            <div className="border-t p-4">
              {!current_user ? (
                <div className="space-y-2">
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={() => router.visit('/session/new')}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    size="sm"
                    onClick={() => router.visit('/registration/new')}
                  >
                    Create Account
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                  size="sm"
                  onClick={() => router.delete('/session')}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
